interface QuarterData {
  quarter: number
  average: number
  total_scores: number
}

interface QuarterlyChartProps {
  data: QuarterData[]
  year: number
  isLoading?: boolean
}

export const QuarterlyChart = ({ data, year, isLoading = false }: QuarterlyChartProps) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    )
  }

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map((d) => Math.abs(d.average)), 1)
  const scale = 100 / maxValue

  // Ensure all 4 quarters exist
  const allQuarters = [1, 2, 3, 4].map((q) => {
    const existing = data.find((d) => d.quarter === q)
    return existing || { quarter: q, average: 0, total_scores: 0 }
  })

  return (
    <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        Evolução Trimestral {year}
      </h3>

      {/* Chart */}
      <div className="flex items-end justify-around h-64 gap-4">
        {allQuarters.map((quarter) => {
          const height = Math.abs(quarter.average) * scale
          const isPositive = quarter.average >= 0

          return (
            <div key={quarter.quarter} className="flex-1 flex flex-col items-center">
              {/* Bar */}
              <div className="w-full flex flex-col items-center justify-end h-full">
                <div className="relative w-full max-w-[80px] flex flex-col items-center justify-end h-full">
                  {/* Value label */}
                  <span
                    className={`text-sm font-semibold mb-1 ${
                      isPositive ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {quarter.average > 0 ? '+' : ''}{quarter.average}
                  </span>

                  {/* Bar */}
                  <div
                    className={`w-full rounded-t transition-all ${
                      isPositive
                        ? 'bg-success hover:bg-success-hover'
                        : 'bg-danger hover:bg-danger-hover'
                    }`}
                    style={{ height: `${height}%`, minHeight: quarter.average !== 0 ? '8px' : '0' }}
                    title={`Q${quarter.quarter}: ${quarter.average > 0 ? '+' : ''}${quarter.average} pts (${quarter.total_scores} avaliações)`}
                  ></div>
                </div>
              </div>

              {/* Quarter label */}
              <div className="mt-3 text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Q{quarter.quarter}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {quarter.total_scores} {quarter.total_scores === 1 ? 'avaliação' : 'avaliações'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-success rounded"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Positivo</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-danger rounded"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Negativo</span>
        </div>
      </div>
    </div>
  )
}

export default QuarterlyChart
